import { FunctionComponent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Modal } from "@/components";
import { InputField } from "@/components/InputField/InputField";
import { TextArea } from "@/components/TextArea/TextArea";
import {
	useCreateMusicTrackMutation,
	useGenerateMusicTrackMutation,
	useUpdateMusicTrackMutation,
} from "@/pages/Private/redux/media/media.api";
import { MusicTrack } from "@/pages/Private/redux/media/media.schema";

type MusicTrackModalMode = "upload" | "generate" | "edit";

interface MusicTrackModalProps {
	track: MusicTrack | null;
	mode: MusicTrackModalMode;
	isOpened: boolean;
	handleClose: () => void;
}

/** Long enough to sit under a thirty-second intro, which is what the wizard makes. */
const DEFAULT_SECONDS = 45;

const emptyForm = {
	name: "",
	description: "",
	licence: "",
	prompt: "",
	durationSeconds: DEFAULT_SECONDS,
};

/**
 * One modal for the three ways a library row changes: an upload, a generation, and an edit.
 *
 * They share every field but two — the file and the prompt — and splitting them into three
 * components would have meant three copies of the licence rule, which is the one field the
 * library cannot do without.
 */
export const MusicTrackModal: FunctionComponent<MusicTrackModalProps> = ({
	track,
	mode,
	isOpened,
	handleClose,
}) => {
	const { t } = useTranslation();
	const ts = (key: string, options?: Record<string, unknown>) =>
		t(`mediaPresets.music.${key}`, options ?? {});

	const [form, setForm] = useState(emptyForm);
	const [file, setFile] = useState<File | null>(null);

	const [createTrack, { isLoading: isCreating }] = useCreateMusicTrackMutation();
	const [updateTrack, { isLoading: isUpdating }] = useUpdateMusicTrackMutation();
	const [generateTrack, { isLoading: isGenerating }] = useGenerateMusicTrackMutation();

	useEffect(() => {
		setForm(
			track
				? {
						name: track.name,
						description: track.description ?? "",
						licence: track.licence ?? "",
						prompt: track.prompt ?? "",
						durationSeconds: track.durationSeconds ?? DEFAULT_SECONDS,
					}
				: emptyForm
		);
		setFile(null);
	}, [track, mode, isOpened]);

	const handleSave = async () => {
		if (mode === "edit" && track) {
			await updateTrack({
				id: track.id,
				name: form.name,
				description: form.description,
				licence: form.licence,
			}).unwrap();
		} else if (mode === "generate") {
			await generateTrack({
				prompt: form.prompt,
				name: form.name,
				description: form.description || undefined,
				durationSeconds: form.durationSeconds,
				licence: form.licence || undefined,
			}).unwrap();
		} else if (file) {
			await createTrack({
				name: form.name,
				description: form.description || undefined,
				licence: form.licence,
				file,
			}).unwrap();
		}

		handleClose();
	};

	// A licence is required on every path but generation, where the server fills in the
	// provider's own terms when the field is left empty.
	const isIncomplete =
		!form.name.trim() ||
		(mode === "upload" && (!file || !form.licence.trim())) ||
		(mode === "edit" && !form.licence.trim()) ||
		(mode === "generate" && !form.prompt.trim());

	return (
		<Modal
			disableSave={isIncomplete}
			handleClose={handleClose}
			handleSave={handleSave}
			isLoading={isCreating || isUpdating || isGenerating}
			isOpened={isOpened}
			size="sm"
			submitButtonText={mode === "generate" ? ts("actions.generateSubmit") : t("basics.save")}
			title={ts(`${mode}Title`)}
		>
			<div className="space-y-3">
				<InputField
					handleChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
					label={ts("fields.name")}
					name="name"
					placeholder={ts("fields.namePlaceholder")}
					value={form.name}
				/>

				<InputField
					handleChange={(event) =>
						setForm((prev) => ({ ...prev, description: event.target.value }))
					}
					label={ts("fields.description")}
					name="description"
					placeholder={ts("fields.descriptionPlaceholder")}
					value={form.description}
				/>

				{mode === "upload" && (
					<label className="block">
						<span className="mb-1.5 block text-xs font-bold uppercase text-subtle">
							{ts("fields.file")}
						</span>
						<input
							accept="audio/*"
							className="w-full rounded-card border border-border bg-white px-3 py-2 text-sm text-text"
							type="file"
							onChange={(event) => setFile(event.target.files?.[0] ?? null)}
						/>
						<span className="mt-1 block text-xs text-subtle">{ts("fields.fileHint")}</span>
					</label>
				)}

				{mode === "generate" && (
					<>
						<TextArea
							handleChange={(event) => setForm((prev) => ({ ...prev, prompt: event.target.value }))}
							label={ts("fields.prompt")}
							maxLength={2000}
							name="prompt"
							placeholder={ts("fields.promptPlaceholder")}
							showError={false}
							value={form.prompt}
						/>
						<InputField
							handleChange={(event) =>
								setForm((prev) => ({ ...prev, durationSeconds: +event.target.value }))
							}
							label={ts("fields.durationSeconds")}
							name="durationSeconds"
							type="number"
							value={form.durationSeconds}
						/>
					</>
				)}

				<InputField
					handleChange={(event) => setForm((prev) => ({ ...prev, licence: event.target.value }))}
					label={ts("fields.licence")}
					name="licence"
					placeholder={
						mode === "generate"
							? ts("fields.licenceGeneratedPlaceholder")
							: ts("fields.licencePlaceholder")
					}
					value={form.licence}
				/>
				<p className="text-xs text-subtle">
					{mode === "generate" ? ts("fields.licenceGeneratedHint") : ts("fields.licenceHint")}
				</p>

				{mode === "generate" && (
					<p className="rounded-card bg-surface px-3 py-2 text-xs text-subtle">
						{ts("generateCostHint")}
					</p>
				)}
			</div>
		</Modal>
	);
};
